import {useEffect, useState, useMemo} from 'react'

import {Box, styled} from '@mui/system'
import {Paper, Typography, CircularProgress, Chip, Stack} from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'

import PageTitle from '../../components/PageTitle'
import DateRangePicker from '../../components/DateRangePicker'

import {OverallForward} from './OverallForward'
import {useCourierBenchmark} from './useCourierBenchmark'
import {OverallReverse} from './OverallReverse'
import {Comparison} from './Comparison'
import {subMonths, startOfMonth, endOfMonth, format} from 'date-fns'
import useTabParam from '../../hooks/useTabParam'
import palette from '../../../theme/palette'

const TAB_TITLES = {
  forward: 'Forward Performance',
  reverse: 'Reverse Performance',
  comparison: 'Service Type Comparison',
}

const PageShell = styled(Box)({
  borderRadius: 16,
  border: `1px solid ${palette.grey[300]}`,
  overflow: 'hidden',
  backgroundColor: palette.common.white,
})

const HeaderBar = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2.5, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  borderRadius: 0,
  boxShadow: 'none',
  borderBottom: `1px solid ${palette.grey[300]}`,
  background: `linear-gradient(135deg, ${palette.background.default} 0%, ${palette.background.neutral} 100%)`,
}))

const HeaderLeft = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

const HeaderIconBox = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: 10,
  background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: palette.common.white,
  flexShrink: 0,
})

const HeaderTextGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

const HeaderTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.05rem',
  color: palette.grey[800],
  lineHeight: 1.3,
})

const HeaderSubtitle = styled(Typography)({
  fontSize: '0.72rem',
  color: palette.grey[600],
  lineHeight: 1.3,
  marginTop: 1,
})

const ContentArea = styled(Box)({
  backgroundColor: palette.background.default,
  minHeight: 400,
})

const LoadingBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 80,
  gap: 16,
})

const LoadingText = styled(Typography)({
  fontSize: '0.82rem',
  color: palette.grey[600],
  fontWeight: 500,
})

const DatePickerWrapper = styled(Box)({
  '& .MuiButton-root': {
    backgroundColor: palette.common.white,
    color: palette.grey[700],
    border: `1px solid ${palette.grey[400]}`,
    borderRadius: 8,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    height: 34,
    padding: '0 14px',
    boxShadow: 'none',
    letterSpacing: 0,
    '&:hover': {
      backgroundColor: palette.background.default,
      borderColor: palette.primary.main,
      boxShadow: 'none',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
    color: palette.grey[600],
  },
})

const DateChip = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.72rem',
  height: 28,
  backgroundColor: palette.common.white,
  color: palette.grey[800],
  border: `1px solid ${palette.grey[400]}`,
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
})

function getDefaultDateRange() {
  const lastMonth = subMonths(new Date(), 1)
  return [
    {
      startDate: startOfMonth(lastMonth),
      endDate: endOfMonth(lastMonth),
      key: 'selection',
    },
  ]
}

export function CourierPerformance() {
  const [view] = useTabParam('forward')
  const [groupOn, setGroupOn] = useState('MOM')
  const [selectedCouriers, setSelectedCouriers] = useState([])
  const [selectedMetrics, setSelectedMetrics] = useState([])
  const [selectedTrendAnalysisMetrics, setSelectedTrendAnalysisMetrics] = useState([])

  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  const maxDate = useMemo(() => new Date(), [])

  const {courierPerformance, forwardBenchmarking, reverseBenchmarking, isLoading} =
    useCourierBenchmark(dateRange, groupOn)

  const dateLabel = useMemo(() => {
    const s = dateRange[0]?.startDate
    const e = dateRange[0]?.endDate
    if (!s || !e) return ''
    return `${format(s, 'dd MMM')} – ${format(e, 'dd MMM yyyy')}`
  }, [dateRange])

  useEffect(() => {
    if (selectedMetrics.length !== 0 && selectedCouriers.length !== 0) {
      setSelectedTrendAnalysisMetrics([selectedMetrics[0]])
    }
  }, [selectedMetrics, selectedCouriers])

  const title = TAB_TITLES[view] || 'Forward Performance'

  return (
    <>
      <PageTitle>D2C Courier Benchmarking</PageTitle>
      <PageShell>
        <HeaderBar>
          <HeaderLeft>
            <HeaderIconBox>
              <LocalShippingIcon sx={{fontSize: 20}} />
            </HeaderIconBox>
            <HeaderTextGroup>
              <HeaderTitle>{title}</HeaderTitle>
              <HeaderSubtitle>
                {view === 'forward' && 'Delivery performance, TAT adherence, and volume metrics for forward shipments'}
                {view === 'reverse' && 'RVP pickup performance and reverse logistics metrics'}
                {view === 'comparison' && 'Side-by-side comparison of service types across key metrics'}
                {!['forward', 'reverse', 'comparison'].includes(view) && 'Analyze delivery performance, TAT adherence, and RVP metrics by service type'}
              </HeaderSubtitle>
            </HeaderTextGroup>
          </HeaderLeft>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            {dateLabel && <DateChip label={dateLabel} size='small' />}
            <DatePickerWrapper>
              <DateRangePicker
                size='small'
                defaultRange={dateRange}
                onChange={setDateRange}
                maxDate={maxDate}
                color='primary'
              />
            </DatePickerWrapper>
          </Stack>
        </HeaderBar>

        {isLoading ? (
          <LoadingBox>
            <CircularProgress size={32} />
            <LoadingText>Fetching performance data...</LoadingText>
          </LoadingBox>
        ) : (
          <ContentArea>
            {view === 'forward' && (
              <OverallForward data={forwardBenchmarking} courierPerformance={courierPerformance} />
            )}
            {view === 'reverse' && <OverallReverse data={reverseBenchmarking} />}
            {view === 'comparison' && (
              <Comparison
                data={courierPerformance}
                selectedCouriers={selectedCouriers}
                selectedMetrics={selectedMetrics}
                selectedTrendAnalysisMetrics={selectedTrendAnalysisMetrics}
                setSelectedCouriers={setSelectedCouriers}
                setSelectedMetrics={setSelectedMetrics}
                setSelectedTrendAnalysisMetrics={setSelectedTrendAnalysisMetrics}
                groupOn={groupOn}
                setGroupOn={setGroupOn}
              />
            )}
          </ContentArea>
        )}
      </PageShell>
    </>
  )
}
